import React, { useEffect } from 'react'
import { noop } from 'lodash'
import _cx from 'classnames/bind'
import PropTypes from 'prop-types'
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  useMap,
} from '@vis.gl/react-google-maps'
import config from '@adc/configment'
import pinSelected from '../../images/map_pin_hover.svg'
import pinUnselected from '../../images/map_pin.svg'
import cls from './index.scss'
const cx = _cx.bind(cls)

const GOOGLE_MAPS_LIBRARIES = ['places']
// US geographic center — the map re-fits to the pins once loaded (see FitBounds)
const US_CENTER_LAT_LNG = { lat: 39.8283, lng: -98.5795 }
const DEFAULT_ZOOM_LEVEL = 4

const Pin = ({ src, active, select = noop, deSelect = noop, ...props }) => (
  <img
    src={src}
    className={cx('pin', {
      active,
    })}
    onClick={select}
    onMouseEnter={select}
    onMouseLeave={deSelect}
    {...props}
  />
)

Pin.propTypes = {
  src: PropTypes.string.isRequired,
  active: PropTypes.bool,
  select: PropTypes.func,
  deSelect: PropTypes.func,
}

/**
 * Imperatively fits the map viewport to all pins once the @vis.gl map instance
 * is ready. Replaces google-map-react's center/zoom-from-fitBounds util.
 */
const FitBounds = ({ coords, pins }) => {
  const map = useMap()
  useEffect(() => {
    if (!map || !window.google) return
    const validCoords = Boolean(coords.lat) && Boolean(coords.lng)
    const all = validCoords ? pins.concat(coords) : pins
    if (!all.length) return
    const bounds = new window.google.maps.LatLngBounds()
    all.forEach(pin =>
      bounds.extend(new window.google.maps.LatLng(pin.lat, pin.lng))
    )
    map.fitBounds(bounds)
  }, [map, coords, pins])
  return null
}

FitBounds.propTypes = {
  coords: PropTypes.object.isRequired,
  pins: PropTypes.array.isRequired,
}

/**
 * Renders pins only once the map/API is ready (useMap() returns the live
 * instance). AdvancedMarker owns positioning; Pin is just the rendered image.
 */
const MapMarkers = ({
  coords,
  pins,
  selectPin,
  deSelectPin,
  selectedProperty,
}) => {
  const gmap = useMap()
  if (!gmap) return null

  const validCoords = Boolean(coords.lat) && Boolean(coords.lng)

  return (
    <>
      {validCoords && (
        <AdvancedMarker
          position={coords}
          zIndex={-1}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
        >
          <Pin src={pinSelected} />
        </AdvancedMarker>
      )}
      {pins.map(pin => (
        <AdvancedMarker
          key={pin.id}
          position={{ lat: pin.lat, lng: pin.lng }}
          anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
          clickable
        >
          <Pin
            src={pinUnselected}
            active={selectedProperty === pin.id}
            select={selectPin(pin.id)}
            deSelect={deSelectPin()}
          />
        </AdvancedMarker>
      ))}
    </>
  )
}

MapMarkers.propTypes = {
  coords: PropTypes.object.isRequired,
  pins: PropTypes.array.isRequired,
  selectPin: PropTypes.func.isRequired,
  deSelectPin: PropTypes.func.isRequired,
  selectedProperty: PropTypes.number.isRequired,
}

const ComparableMap = ({
  coords,
  properties,
  selectPin,
  deSelectPin,
  selectedProperty,
}) => {
  const pins = properties
    .map((property, i) => ({
      lat: property.info.geocode.latitude,
      lng: property.info.geocode.longitude,
      id: i,
    }))
    .sort((a, b) => a.lng - b.lng) // We sort the pins so that they overlap correctly on the map, so pins on the bottom are always in front of pins above them

  return (
    <div
      style={{ height: '300px', width: '100%', position: 'relative' }}
      className={cx('u-mt-1')}
    >
      <APIProvider
        apiKey={config.get('GOOGLE_MAPS_API_KEY')}
        channel={config.get('GOOGLE_MAPS_CHANNEL_NAME')}
        libraries={GOOGLE_MAPS_LIBRARIES}
        language='en'
        version='weekly'
      >
        <GoogleMap
          mapId={config.get('GOOGLE_MAPS_MAP_ID', 'DEMO_MAP_ID')}
          defaultCenter={US_CENTER_LAT_LNG}
          defaultZoom={DEFAULT_ZOOM_LEVEL}
          gestureHandling='greedy'
          streetViewControl
          zoomControl
          fullscreenControl={false}
          style={{ width: '100%', height: '100%' }}
        >
          <FitBounds coords={coords} pins={pins} />
          <MapMarkers
            coords={coords}
            pins={pins}
            selectPin={selectPin}
            deSelectPin={deSelectPin}
            selectedProperty={selectedProperty}
          />
        </GoogleMap>
      </APIProvider>
    </div>
  )
}

ComparableMap.propTypes = {
  coords: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  properties: PropTypes.object.isRequired,
  selectPin: PropTypes.func.isRequired,
  deSelectPin: PropTypes.func.isRequired,
  selectedProperty: PropTypes.number.isRequired,
}

export default React.memo(ComparableMap)
